import { TrackingEventState } from "@swyft/swyft-common";
import classNames from "classnames";
import { Typography } from "@helo/ui";

import { format, formatTime } from "~/helpers/date";
import { IStepData } from "~/helpers/components/steps/step";
import variables from "~/assets/scss/variables.module.scss";

interface IStepProps {
  isSummary?: boolean;
  final?: boolean;
  data: IStepData;
  timezone?: string;
}

const Step = (props: IStepProps) => {
  const { isSummary = false, final = false, data, timezone } = props;
  const { title, icon: Icon, timestamp, details, state } = data;

  // derived state
  const isFailed = state === TrackingEventState.FAILED || state === TrackingEventState.DELAYED;
  const isProblem = state === TrackingEventState.PROBLEM || state === TrackingEventState.RETURNING_TO_SENDER;
  const isCanceled = state === TrackingEventState.CANCELED;
  const isCompleted = timestamp && !isFailed && !isProblem && !isCanceled;
  const isUpcoming = !timestamp;
  const showTimestamp =
    timestamp && state !== TrackingEventState.RETURNING_TO_SENDER;

  const getStateClasses = (baseClass: string) => [
    isCompleted && `${baseClass}--completed`,
    isFailed && `${baseClass}--failed`,
    (isProblem || isCanceled) && `${baseClass}--problem`,
    isUpcoming && `${baseClass}--next`,
    final && `${baseClass}--final`,
  ];

  return (
    <div
      className={classNames("step", isSummary ? "step--short" : "step--long")}
    >
      <div className={classNames("step-info", ...getStateClasses("step-info"))}>
        <div
          className={classNames("step-icon", ...getStateClasses("step-icon"))}
        >
          {typeof Icon === "object" ? (
            /** If Icon is PNG or SVG file show Image Component */
            <img alt={title} src={Icon.src} className="step-icon__img" />
          ) : (
            /** Otherwise Show the component provided as Prop */
            <Icon color={variables.primaryDark} className="step-icon__icon" />
          )}
        </div>
        <div
          className={classNames("step-text", ...getStateClasses("step-text"))}
        >
          <Typography
            className="step-text__title"
            variant="h6"
            component="h4"
            sx={({ palette }) => ({
              color: palette.secondary.main,
              fontWeight: 700,
            })}
          >
            {title}
          </Typography>
          {isSummary ? (
            showTimestamp && (
              <Typography
                className="timelog"
                variant="body2"
                component="div"
                sx={({ palette }) => ({
                  color: palette.neutralScale[400],
                  display: "block",
                })}
              >
                <Typography
                  className="timelog__date"
                  sx={{ display: "block " }}
                >
                  {format(timestamp, timezone)}
                </Typography>
                <Typography
                  className="timelog__time"
                  sx={{ display: "block " }}
                >
                  {formatTime(timestamp, timezone)}
                </Typography>
              </Typography>
            )
          ) : (
            <Typography
              variant="body1"
              sx={({ palette }) => ({ color: palette.neutralScale[500] })}
            >
              <Typography component="span" sx={{ mb: 1, display: "block" }}>
                {details}
              </Typography>
              {showTimestamp && (
                <Typography
                  component="span"
                  sx={({ palette }) => ({
                    mb: 1,
                    display: "block",
                    fontWeight: 600,
                    color: palette.neutralScale[500],
                  })}
                >
                  {format(timestamp, timezone)} |{" "}
                  {formatTime(timestamp, timezone)}
                </Typography>
              )}
            </Typography>
          )}
        </div>
      </div>
    </div>
  );
};

export default Step;
